/**
 * Single point of contact for the Electron preload bridge.
 *
 * Older shell versions ship a `window.electron` object that is missing
 * methods added in newer renderers. Reading `window.electron.foo` directly
 * crashes those shells at launch. All renderer code must go through this
 * module — the ESLint rule in `.eslintrc.cjs` enforces it.
 *
 * Each wrapped method:
 *   1. No-ops when not running inside Electron at all.
 *   2. Feature-detects the specific method on the bridge.
 *   3. Reports the missing method to Sentry once per session (tagged with
 *      the shell version) when the shell is new enough to have shipped it.
 */
import { Sentry } from "@/telemetry/sentry"

type ElectronBridge = NonNullable<Window["electron"]>

/** Shell release that first shipped each method; methods absent here are expected in every shell. */
const SHELL_VERSION_ADDING_API: Partial<Record<keyof ElectronBridge, string>> = {
	setPreventDisplaySleep: "1.1.1",
	onSystemSuspend: "1.1.1",
	onSystemResume: "1.1.1",
	getAppConfig: "1.2.0",
	setShellLocale: "1.2.0",
}

const bridge: ElectronBridge | undefined =
	typeof window !== "undefined" ? window.electron : undefined

export const electron: ElectronBridge | undefined = bridge
export const isElectronApp = !!bridge

const warned = new Set<string>()

/** Prerelease and build suffixes are ignored: `1.2.0-beta.1` ships whatever `1.2.0` ships. */
function parseVersion(version: string): [number, number, number] | undefined {
	const match = /^(\d+)\.(\d+)\.(\d+)(?:[-+.]|$)/.exec(version)
	return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : undefined
}

/** Unparseable versions report rather than stay silent, so a broken shell still surfaces. */
function shellPredatesApi(method: keyof ElectronBridge, shellVersion: string): boolean {
	const addedIn = SHELL_VERSION_ADDING_API[method]
	if (!addedIn) return false
	const shell = parseVersion(shellVersion)
	const required = parseVersion(addedIn)
	if (!shell || !required) return false
	const [shellMajor, shellMinor, shellPatch] = shell
	const [requiredMajor, requiredMinor, requiredPatch] = required
	if (shellMajor !== requiredMajor) return shellMajor < requiredMajor
	if (shellMinor !== requiredMinor) return shellMinor < requiredMinor
	return shellPatch < requiredPatch
}

function reportMissing(method: keyof ElectronBridge) {
	if (!bridge || warned.has(method)) return
	warned.add(method)
	if (shellPredatesApi(method, bridge.shellVersion)) return
	Sentry.captureMessage(`Electron bridge missing API: ${method}`, {
		level: "warning",
		tags: { electron_shell_version: bridge.shellVersion },
	})
}

export function getShellVersion(): string | undefined {
	return bridge?.shellVersion
}

export function getAppUrl(): string | undefined {
	return bridge?.appUrl
}

export function getCustomProtocol(): string | undefined {
	return bridge?.customProtocol
}

export function notifyAppReady(version: string): void {
	if (!bridge) return
	if (typeof bridge.notifyAppReady !== "function") {
		reportMissing("notifyAppReady")
		return
	}
	bridge.notifyAppReady(version)
}

export function getTemporaryUserId(): Promise<string> | undefined {
	if (!bridge) return undefined
	if (typeof bridge.getTemporaryUserId !== "function") {
		reportMissing("getTemporaryUserId")
		return undefined
	}
	return bridge.getTemporaryUserId()
}

export function getElectronAppConfig(): Promise<unknown> | undefined {
	if (!bridge) return undefined
	if (typeof bridge.getAppConfig !== "function") {
		reportMissing("getAppConfig")
		return undefined
	}
	return bridge.getAppConfig()
}

export function setElectronShellLocale(locale: string): Promise<string> | undefined {
	if (!bridge) return undefined
	if (typeof bridge.setShellLocale !== "function") {
		reportMissing("setShellLocale")
		return undefined
	}
	return bridge.setShellLocale(locale)
}

export function setPreventDisplaySleep(enabled: boolean): Promise<void> | undefined {
	if (!bridge) return undefined
	if (typeof bridge.setPreventDisplaySleep !== "function") {
		reportMissing("setPreventDisplaySleep")
		return undefined
	}
	return bridge.setPreventDisplaySleep(enabled)
}

export function onSystemSuspend(callback: () => void): void {
	if (!bridge) return
	if (typeof bridge.onSystemSuspend !== "function") {
		reportMissing("onSystemSuspend")
		return
	}
	bridge.onSystemSuspend(callback)
}

export function onSystemResume(callback: () => void): void {
	if (!bridge) return
	if (typeof bridge.onSystemResume !== "function") {
		reportMissing("onSystemResume")
		return
	}
	bridge.onSystemResume(callback)
}

export function onEnterFullScreen(callback: () => void): void {
	if (!bridge) return
	if (typeof bridge.onEnterFullScreen !== "function") {
		reportMissing("onEnterFullScreen")
		return
	}
	bridge.onEnterFullScreen(callback)
}

export function onLeaveFullScreen(callback: () => void): void {
	if (!bridge) return
	if (typeof bridge.onLeaveFullScreen !== "function") {
		reportMissing("onLeaveFullScreen")
		return
	}
	bridge.onLeaveFullScreen(callback)
}

export function onCheckoutComplete(callback: () => void): void {
	if (!bridge) return
	if (typeof bridge.onCheckoutComplete !== "function") {
		reportMissing("onCheckoutComplete")
		return
	}
	bridge.onCheckoutComplete(callback)
}
